const PARQUET_MAGIC = 'PAR1';
const MAGIC_BYTES = 4;
const FOOTER_LEN_BYTES = 4;
const TAIL_BYTES = MAGIC_BYTES + FOOTER_LEN_BYTES;


/**
 * A Parquet file stores column metadata in its footer.
 * This function reads the last few bytes of the file to determine
 * the footer size, then returns a Blob containing only the
 * metadata portion needed for upload.
 */
export async function extractParquetMetadata(file) {
    if (!file || typeof file.slice !== 'function') {
        throw new Error('extractParquetMetadata expects a File or Blob');
    }
    if (file.size < MAGIC_BYTES + TAIL_BYTES) {
        throw new Error('File too small to be a Parquet file');
    }

    const tail = new Uint8Array(
        await file.slice(file.size - TAIL_BYTES).arrayBuffer()
    );

    const trailingMagic = new TextDecoder().decode(tail.subarray(FOOTER_LEN_BYTES));
    if (trailingMagic !== PARQUET_MAGIC) {
        throw new Error(`Not a Parquet file (trailing magic = "${trailingMagic}")`);
    }

    const footerLen = new DataView(tail.buffer).getUint32(0, true);
    const footerStart = file.size - TAIL_BYTES - footerLen;
    if (footerStart < MAGIC_BYTES) {
        throw new Error(
            `Invalid Parquet footer length (${footerLen}) for file size ${file.size}`
        );
    }

    return new Blob(
        [
            file.slice(0, MAGIC_BYTES),
            file.slice(footerStart, file.size),
        ],
        { type: 'application/octet-stream' }
    );
}

export async function extractParquetMetadataFile(file, suffix = '.metadata.parquet') {
    const blob = await extractParquetMetadata(file);
    const base = file.name?.replace(/\.parquet$/i, '') || 'file';
    return new File([blob], `${base}${suffix}`, {
        type: 'application/octet-stream',
        lastModified: file.lastModified,
    });
}


export async function extractParquetBase64(file) {
    const metadata = await extractParquetMetadata(file);
    const buffer = await metadata.arrayBuffer();
    const bytesBuffer = new Uint8Array(buffer);
    const base64 = bytesBuffer.toBase64();
    return base64;
}
